<template>
  <div class="page">
    <Navbar />
    <main class="container">
      <!-- Start or join -->
      <section class="hero">
        <div class="intro">
          <p class="eyebrow">{{ hello }}, {{ firstName }}</p>
          <h1>Meet, teach and learn — live.</h1>
          <p class="lead">
            Start a meeting in one click, or join one with a code. Clear video for everyone,
            from a small group to a full hall.
          </p>
          <div class="actions">
            <button class="btn btn-primary btn-lg" @click="instant" :disabled="starting">
              <span v-if="starting" class="spinner"></span>
              <template v-else><i class="ri-flashlight-fill"></i> Start now</template>
            </button>
            <button class="btn btn-secondary btn-lg" @click="form = { schedule: true }">
              <i class="ri-calendar-event-line"></i> Schedule
            </button>
          </div>
        </div>

        <form class="join card" @submit.prevent="join">
          <div class="join-head">
            <span class="join-icon"><i class="ri-keyboard-line"></i></span>
            <div>
              <h2>Join with a code</h2>
              <p>Paste the code or the whole link you were sent.</p>
            </div>
          </div>
          <div class="join-row">
            <input class="input" v-model.trim="code" placeholder="abc-defg-hij" aria-label="Meeting code or link" />
            <button class="btn btn-primary" :disabled="!codeFromInput">Join</button>
          </div>
        </form>
      </section>

      <!-- Happening now -->
      <section v-if="liveNow.length" class="now">
        <h2 class="section-title"><span class="dot"></span> Happening now</h2>
        <div class="now-row scroll-thin">
          <router-link
            v-for="m in liveNow"
            :key="m.id"
            :to="`/${m.code}/preview`"
            class="now-card"
          >
            <div>
              <strong>{{ m.title }}</strong>
              <small>{{ m.mode === "webinar" ? "Class / webinar" : "Meeting" }}</small>
            </div>
            <span class="btn btn-sm btn-danger"><i class="ri-vidicon-line"></i>Join</span>
          </router-link>
        </div>
      </section>

      <!-- Your meetings -->
      <section class="list">
        <div class="list-head">
          <h2 class="section-title">Your meetings</h2>
          <div class="tools">
            <div class="input-icon search">
              <i class="ri-search-line"></i>
              <input class="input" v-model="search" @input="debounced" placeholder="Search meetings" />
            </div>
            <select class="input sort" v-model="sort" @change="reload" aria-label="Sort">
              <option value="created_at:desc">Newest first</option>
              <option value="created_at:asc">Oldest first</option>
              <option value="updated_at:desc">Recently updated</option>
            </select>
          </div>
        </div>

        <div class="tabs" role="tablist">
          <button
            v-for="t in tabs"
            :key="t.value"
            role="tab"
            :class="{ active: tab === t.value }"
            @click="setTab(t.value)"
          >
            {{ t.label }}
          </button>
        </div>

        <div class="grid" v-if="loading">
          <div class="card skeleton-card" v-for="i in 6" :key="i">
            <div class="skeleton" style="width: 40px; height: 40px; border-radius: 12px"></div>
            <div class="skeleton" style="width: 70%; height: 16px; margin-top: 14px"></div>
            <div class="skeleton" style="width: 95%; height: 12px; margin-top: 10px"></div>
            <div class="skeleton" style="width: 60%; height: 12px; margin-top: 6px"></div>
            <div class="skeleton" style="width: 100%; height: 30px; margin-top: 22px"></div>
          </div>
        </div>

        <TransitionGroup v-else-if="meetings.length" tag="div" class="grid" name="fade">
          <MeetingCard
            v-for="m in meetings"
            :key="m.id"
            :meeting="m"
            :me="me.id"
            @copy="copy"
            @edit="form = m"
            @people="managing = m"
            @delete="deleting = m"
          />
        </TransitionGroup>

        <div class="empty card" v-else>
          <img src="@/assets/img/no-meeting.svg" alt="" />
          <h3>{{ emptyTitle }}</h3>
          <p>{{ emptyText }}</p>
          <button class="btn btn-primary" v-if="!search && tab === 'all'" @click="form = {}">
            <i class="ri-add-line"></i> New meeting
          </button>
        </div>

        <div ref="more" class="more">
          <span v-if="loadingMore" class="spinner"></span>
        </div>
      </section>
    </main>

    <MeetingForm
      v-if="form"
      :meeting="form.live_id ? form : null"
      :schedule="!!form.schedule"
      @close="form = null"
      @saved="saved"
    />
    <ManagePeople v-if="managing" :meeting="managing" @close="managing = null" @changed="reload" />
    <ConfirmDialog
      v-if="deleting"
      danger
      :title="`Delete “${deleting.title}”?`"
      message="The meeting and its link stop working for everyone. This can't be undone."
      confirm-text="Delete meeting"
      :on-confirm="destroy"
      @close="deleting = null"
    />

    <Modal v-if="ready" size="sm" title="Your meeting is ready" subtitle="Share this link with the people you want to join." @close="ready = null">
      <div class="ready">
        <div class="link-box">
          <i class="ri-link"></i>
          <span>{{ ready.link }}</span>
          <button class="btn btn-sm btn-soft" @click="copy(ready)"><i class="ri-file-copy-line"></i>Copy</button>
        </div>
        <p class="hint" v-if="ready.scheduled_at">
          Scheduled for {{ whenOf(ready.scheduled_at) }}. Everyone invited has it in their calendar.
        </p>
      </div>
      <template #footer>
        <button class="btn btn-secondary" @click="ready = null">Done</button>
        <router-link class="btn btn-primary" :to="`/${ready.code}/preview`"><i class="ri-vidicon-line"></i>Join now</router-link>
      </template>
    </Modal>
  </div>
</template>

<script>
import Navbar from "@/components/Navbar.vue";
import MeetingCard from "@/components/MeetingCard.vue";
import MeetingForm from "@/components/MeetingForm.vue";
import ManagePeople from "@/components/ManagePeople.vue";
import Modal from "@/components/ui/Modal.vue";
import ConfirmDialog from "@/components/ui/ConfirmDialog.vue";
import { live, errorMessage } from "@/api";
import { currentUser } from "@/session";
import { greeting, when } from "@/format";

const PAGE = 12;

// codeOf takes a code out of whatever was pasted: a code, or a link to one.
function codeOf(text) {
  if (!text) return "";
  let code = text.trim();
  if (code.includes("/")) code = code.split("/").filter(Boolean).pop();
  return code.split("?")[0].split("#")[0].toLowerCase();
}

export default {
  name: "Home",
  components: { Navbar, MeetingCard, MeetingForm, ManagePeople, Modal, ConfirmDialog },

  data() {
    return {
      me: currentUser(),
      code: "",
      meetings: [],
      liveNow: [],
      cursor: null,
      hasMore: false,
      loading: true,
      loadingMore: false,
      search: "",
      sort: "created_at:desc",
      tab: "all",
      tabs: [
        { value: "all", label: "All" },
        { value: "live", label: "Live now" },
        { value: "mine", label: "Created by me" },
      ],
      timer: null,
      observer: null,
      form: null, // {} to create, a meeting to edit
      managing: null,
      deleting: null,
      ready: null,
      starting: false,
    };
  },

  computed: {
    firstName() {
      return this.me.first_name || "there";
    },
    hello() {
      return greeting();
    },
    codeFromInput() {
      return codeOf(this.code);
    },
    emptyTitle() {
      if (this.search) return "No meetings match your search";
      if (this.tab === "live") return "Nothing is live right now";
      return "No meetings yet";
    },
    emptyText() {
      if (this.search) return "Try a different word, or clear the search.";
      if (this.tab === "live") return "Meetings appear here as soon as someone joins.";
      return "Meetings you create or are invited to will show up here.";
    },
  },

  mounted() {
    this.reload();
    this.loadLive();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && this.hasMore && !this.loading && !this.loadingMore) this.load(true);
      },
      { rootMargin: "200px" }
    );
    this.observer.observe(this.$refs.more);
  },

  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.timer);
  },

  methods: {
    whenOf: when,

    toast(e) {
      this.$toast.error(errorMessage(e), { position: "top" });
    },

    params() {
      const [sortBy, order] = this.sort.split(":");
      const p = { limit: PAGE, sort_by: sortBy, order };
      if (this.search.trim()) p.search = this.search.trim();
      if (this.tab === "live") p.status = "live";
      if (this.tab === "mine") p.mine = true;
      if (this.cursor) p.cursor = this.cursor;
      return p;
    },

    async load(more) {
      if (more) this.loadingMore = true;
      else {
        this.loading = true;
        this.cursor = null;
      }
      try {
        const response = await live.list(this.params());
        const page = response.data.data.lives;
        const docs = page.docs || [];
        this.meetings = more ? [...this.meetings, ...docs] : docs;
        this.cursor = page.next_cursor;
        this.hasMore = !!page.has_more;
      } catch (e) {
        this.toast(e);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    reload() {
      this.load(false);
    },

    async loadLive() {
      try {
        const response = await live.list({ status: "live", limit: 10 });
        this.liveNow = response.data.data.lives.docs || [];
      } catch (e) {
        this.liveNow = [];
      }
    },

    debounced() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.reload, 300);
    },

    setTab(tab) {
      this.tab = tab;
      this.reload();
    },

    join() {
      if (this.codeFromInput) this.$router.push(`/${this.codeFromInput}/preview`);
    },

    // instant makes a meeting and goes straight to its pre-join screen.
    async instant() {
      this.starting = true;
      try {
        const name = this.me.first_name ? `${this.me.first_name}'s meeting` : "Instant meeting";
        const response = await live.create({ title: name, public: false });
        this.$router.push(`/${response.data.data.live.code}/preview`);
      } catch (e) {
        this.toast(e);
        this.starting = false;
      }
    },

    saved(meeting) {
      const created = !(this.form && this.form.live_id);
      this.form = null;
      this.reload();
      if (created) this.ready = meeting;
    },

    async copy(m) {
      try {
        await navigator.clipboard.writeText(m.link);
        this.$toast.success("Link copied", { position: "top", duration: 2000 });
      } catch (e) {
        this.$toast.error("Couldn't copy the link", { position: "top" });
      }
    },

    async destroy() {
      try {
        await live.destroy(this.deleting.live_id);
        this.$toast.success("Meeting deleted", { position: "top", duration: 2500 });
        this.meetings = this.meetings.filter((m) => m.id !== this.deleting.id);
        this.loadLive();
      } catch (e) {
        this.toast(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
  gap: 2rem;
  align-items: center;
  padding: 1.5rem 0 2.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding-top: 0.5rem;
  }
}

.eyebrow {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--brand);
}

h1 {
  margin-top: 0.5rem;
  font-size: clamp(1.9rem, 3.6vw, 2.75rem);
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.lead {
  margin-top: 0.9rem;
  max-width: 34rem;
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--ink-2);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.6rem;
}

.join {
  padding: 1.4rem;
  box-shadow: var(--shadow);

  .join-head {
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
  }

  .join-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: var(--brand-100);
    color: var(--brand);
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  h2 {
    font-size: 1.05rem;
    font-weight: 600;
  }

  p {
    margin-top: 0.2rem;
    font-size: 0.86rem;
    color: var(--ink-3);
  }

  .join-row {
    display: flex;
    gap: 0.5rem;
    margin-top: 1.1rem;

    .input {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      letter-spacing: 0.02em;
    }

    .btn {
      height: 44px;
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--danger);
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.15);
    animation: pulse 1.6s ease-in-out infinite;
  }
}

.now {
  margin-bottom: 2.25rem;

  .now-row {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.85rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
  }

  .now-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 0 0 auto;
    min-width: 260px;
    padding: 0.85rem 0.9rem 0.85rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid #f3c4c4;
    background: linear-gradient(135deg, #fff5f5, #fff);
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: var(--shadow);
    }

    > div {
      flex: 1;
      min-width: 0;
    }

    strong {
      display: block;
      font-size: 0.92rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      font-size: 0.78rem;
      color: var(--ink-3);
    }
  }
}

.list-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.tools {
  display: flex;
  gap: 0.5rem;

  .search {
    width: 260px;
  }

  .sort {
    width: 170px;
  }

  @media (max-width: 640px) {
    width: 100%;

    .search {
      flex: 1;
      width: auto;
    }

    .sort {
      width: 140px;
    }
  }
}

.tabs {
  display: flex;
  gap: 0.25rem;
  margin: 1.1rem 0 1.1rem;
  border-bottom: 1px solid var(--line);

  button {
    position: relative;
    padding: 0.65rem 0.85rem;
    border: none;
    background: none;
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--ink-3);
    cursor: pointer;

    &:hover {
      color: var(--ink);
    }

    &.active {
      color: var(--brand);

      &::after {
        content: "";
        position: absolute;
        left: 0.5rem;
        right: 0.5rem;
        bottom: -1px;
        height: 2px;
        border-radius: 2px;
        background: var(--brand);
      }
    }
  }
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.skeleton-card {
  padding: 1.1rem;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1.5rem;
  text-align: center;

  img {
    width: 150px;
    opacity: 0.9;
  }

  h3 {
    margin-top: 1.2rem;
    font-size: 1.1rem;
    font-weight: 700;
  }

  p {
    margin-top: 0.35rem;
    color: var(--ink-3);
    max-width: 26rem;
  }

  .btn {
    margin-top: 1.2rem;
  }
}

.more {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
  color: var(--brand);
}

.ready {
  .link-box {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.5rem 0.5rem 0.85rem;
    border: 1px solid var(--line-2);
    border-radius: var(--radius);
    background: var(--canvas);

    > i {
      color: var(--ink-3);
    }

    span {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85rem;
    }
  }

  .hint {
    margin-top: 0.8rem;
    font-size: 0.85rem;
    color: var(--ink-3);
  }
}
</style>
